import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Network, CheckCircle, Download, Calendar, DollarSign, Users, ArrowRight, Shield, Monitor } from 'lucide-react';

const SystemNetworkEngineerPage = () => {
  const weeks = [
    {
      week: 'Week 1',
      title: 'Networking Concepts Fundamentals (Part 1)',
      domain: 'Network+ Domain: Networking Concepts (23%)',
      topics: ['OSI model', 'TCP/IP stack', 'Ports & protocols', 'IPv4 fundamentals', 'Public vs private, APIPA, RFC1918', 'Subnetting basics', 'VLSM introduction'],
      lab: 'Packet Tracer fundamentals'
    },
    {
      week: 'Week 2',
      title: 'Networking Concepts (Part 2)',
      topics: ['IPv4 subnetting mastery', 'CIDR notation', 'Address classes', 'Transmission media (fiber, copper, wireless)', 'Connectors (SC, LC, ST, RJ45, MPO, BNC)', 'Network topologies (star, mesh, collapsed core, spine-leaf)', 'Traffic types (unicast/multicast/broadcast/anycast)'],
      lab: 'VLAN & subnetting challenges'
    },
    {
      week: 'Week 3',
      title: 'Network Implementation (Part 1)',
      domain: 'Network+ Domain: Network Implementation (20%)',
      topics: ['Static vs dynamic routing', 'OSPF, EIGRP, BGP basics', 'NAT, PAT', 'VLANs & trunking'],
      lab: 'Multi-router OSPF lab'
    },
    {
      week: 'Week 4',
      title: 'Network Implementation (Part 2)',
      topics: ['STP', 'MTU & jumbo frames', 'Wireless fundamentals', 'WPA2/WPA3', 'AP placement & coverage', 'Guest networks'],
      lab: 'Wireless optimization'
    },
    {
      week: 'Week 5',
      title: 'Network Operations (Part 1)',
      domain: 'Network+ Domain: Network Operations (19%)',
      topics: ['Documentation: logical/physical diagrams, IPAM', 'SLAs & lifecycle (EOL/EOS)', 'Change management', 'Configuration backups & baselines'],
      lab: 'Network documentation project'
    },
    {
      week: 'Week 6',
      title: 'Network Operations (Part 2)',
      topics: ['SNMP monitoring', 'Packet capture', 'Flow data', 'Log aggregation', 'API integration', 'DR: RPO, RTO, MTTR, MTBF'],
      lab: 'Wireshark packet captures'
    },
    {
      week: 'Week 7',
      title: 'Network Services & Access Management',
      topics: ['DHCP, SLAAC, DNS', 'NTP, PTP, NTS', 'VPNs', 'SSH, RDP', 'GUI/API/Console'],
      lab: 'DHCP/DNS/VPN configuration'
    },
    {
      week: 'Week 8',
      title: 'Network Security (Part 1)',
      domain: 'Network+ Domain: Network Security (14%)',
      topics: ['Encryption', 'PKI', 'MFA, SSO', 'RADIUS, TACACS+, LDAP, SAML', 'IAM principles', 'Device hardening'],
      lab: 'ACL & SSH hardening'
    },
    {
      week: 'Week 9',
      title: 'Network Security (Part 2)',
      topics: ['Physical security', 'Honeypots/honeynets', 'CIA, risk, vulnerability, exploit', 'PCI DSS, GDPR', 'Segmentation (IoT, OT, SCADA, BYOD)', 'Attacks: DoS/DDoS, VLAN hopping, DNS spoofing, ARP poisoning, Evil Twin, rogue AP, MITM'],
      lab: 'Rogue AP detection'
    },
    {
      week: 'Week 10',
      title: 'Network Troubleshooting (Part 1)',
      domain: 'Network+ Domain: Troubleshooting (24%)',
      topics: ['Troubleshooting methodology', 'Cable & interface issues', 'PoE & transceiver mismatch', 'Routing & ACL issues'],
      lab: 'Layer 1-3 troubleshooting'
    },
    {
      week: 'Week 11',
      title: 'Network Troubleshooting (Part 2)',
      topics: ['Packet loss, latency, jitter', 'Wireless interference', 'IP pool exhaustion', 'Incorrect gateway/subnet'],
      tools: 'Protocol analyzers, Wi-Fi analyzers, cable testers, CLI tools',
      lab: 'Enterprise troubleshooting challenge'
    },
    {
      week: 'Week 12',
      title: 'Exam Prep, Mock Test & Career Portfolio',
      topics: ['Complete exam revision', '100-question mock exam', 'Weak area refinement', 'Ticket resolution simulations', 'Resume & LinkedIn setup', 'Pathways to NOC/IT Support/Network Engineer roles'],
      outcome: 'Ready to pass Network+ and apply for job roles immediately'
    }
  ];

  const examDomains = [
    { name: 'Networking Concepts', percentage: '23%' },
    { name: 'Network Implementation', percentage: '20%' },
    { name: 'Network Operations', percentage: '19%' },
    { name: 'Network Security', percentage: '14%' },
    { name: 'Network Troubleshooting', percentage: '24%' }
  ];

  const skills = [
    'Subnetting',
    'VLANs & Routing',
    'Wireless optimization',
    'Network monitoring',
    'Packet analysis',
    'ACLs',
    'VPNs',
    'Troubleshooting methodology',
    'IAM & Security controls',
    'Network documentation',
    'Cloud networking fundamentals'
  ];

  const tools = [
    'Cisco Packet Tracer',
    'Wireshark',
    'Linux',
    'Windows/macOS',
    'VS Code',
    'SNMP/NetFlow tools',
    'CLI tools (ping, traceroute, nslookup, tcpdump)',
    'VPN clients',
    'Cloud VPC basics'
  ];

  const careers = [
    'IT Support',
    'Service Desk',
    'Network Technician',
    'Junior System Administrator',
    'NOC Technician',
    'Pathway to Cloud/DevOps'
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden" style={{ background: 'linear-gradient(135deg, #020617 0%, #0B1120 100%)' }}>
        <div className="max-w-[1400px] mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <span className="px-4 py-1.5 rounded-full text-sm font-semibold bg-[#2563EB] text-white">12 Weeks</span>
                <span className="px-4 py-1.5 rounded-full text-sm font-semibold bg-[#16A34A] text-white">Beginner Friendly</span>
                <span className="px-4 py-1.5 rounded-full text-sm font-semibold bg-[#6366F1] text-white">Live Mentor Support</span>
              </div>
              
              <h1 className="text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>
                System & Network Engineer Program (12 Weeks)
              </h1>
              
              <p className="text-xl text-[#C7D2FE] mb-8">
                Kick-Start Your IT Career - Master Networking, Cloud, Security, Troubleshooting, and Pass the CompTIA Network+ Certification
              </p>
              
              <div className="flex items-center gap-4 mb-8 text-white">
                <div className="flex items-center gap-2">
                  <DollarSign size={20} />
                  <span className="font-semibold">$65,000 - $90,000</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar size={20} />
                  <span>1.5-2 hrs/day</span>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white font-semibold transition-all">
                  Enroll Now
                  <ArrowRight size={20} />
                </Link>
                <button className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border-2 border-white/30 hover:border-white/60 hover:bg-white/5 text-white font-semibold transition-all">
                  <Download size={20} />
                  Download Course PDF
                </button>
              </div>
            </div>
            
            <div className="relative">
              <div className="rounded-[24px] p-8 border border-white/10 backdrop-blur-md" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)' }}>
                <Network size={200} className="text-[#2563EB] mx-auto mb-4" strokeWidth={1} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Program Overview */}
      <section className="py-20 px-4 bg-[#F9FAFB]">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>
              Program Overview
            </h2>
            <p className="text-lg text-[#4B5563] max-w-4xl mx-auto leading-relaxed">
              Become job-ready as a System & Network Engineer in just 12 weeks. This program is designed for complete beginners and career changers, aligned 1:1 with the CompTIA Network+ (N10-009) exam blueprint. Through structured weekly modules, hands-on labs, troubleshooting tickets, cloud networking skills, security fundamentals, and full exam preparation, you will gain the technical depth and confidence needed for real-world IT roles.
            </p>
          </div>
        </div>
      </section>

      {/* 12-Week Curriculum */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-12 text-center" style={{ fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>
            12-Week Curriculum
          </h2>
          
          <div className="space-y-6">
            {weeks.map((week, idx) => (
              <div key={idx} className="rounded-[20px] p-6 bg-white border border-[#E5E7EB] hover:shadow-lg transition-all">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0 w-24 text-center">
                    <div className="inline-flex px-4 py-2 rounded-full bg-[#2563EB] text-white font-bold text-sm">
                      {week.week}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-[#0F172A] mb-2">{week.title}</h3>
                    {week.domain && (
                      <p className="text-sm text-[#2563EB] font-semibold mb-3">{week.domain}</p>
                    )}
                    <ul className="space-y-2 mb-4">
                      {week.topics.map((topic, i) => (
                        <li key={i} className="flex items-start gap-2 text-[#4B5563]">
                          <CheckCircle size={16} className="text-[#2563EB] flex-shrink-0 mt-1" />
                          <span>{topic}</span>
                        </li>
                      ))}
                    </ul>
                    {week.lab && (
                      <div className="mb-3 p-3 rounded-xl bg-[#EFF6FF]">
                        <p className="text-sm font-semibold text-[#2563EB] mb-1">Lab:</p>
                        <p className="text-sm text-[#0F172A]">{week.lab}</p>
                      </div>
                    )}
                    {week.tools && (
                      <div className="mb-3 p-3 rounded-xl bg-[#F0FDF4]">
                        <p className="text-sm font-semibold text-[#16A34A] mb-1">Tools:</p>
                        <p className="text-sm text-[#0F172A]">{week.tools}</p>
                      </div>
                    )}
                    {week.outcome && (
                      <div className="text-sm text-[#6B7280] mt-3">
                        <strong>Outcome:</strong> {week.outcome}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Network+ Exam Objectives */}
      <section className="py-20 px-4 bg-[#F9FAFB]">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>
              Network+ Exam Objectives Summary
            </h2>
            <p className="text-lg text-[#4B5563] mb-8">
              This program aligns with the CompTIA Network+ (N10-009) certification exam
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {examDomains.map((domain, idx) => (
              <div key={idx} className="p-6 rounded-[20px] bg-white border border-[#E5E7EB] hover:shadow-md transition-all">
                <div className="flex items-center justify-between mb-2">
                  <Shield size={24} className="text-[#2563EB]" />
                  <span className="text-2xl font-bold text-[#2563EB]">{domain.percentage}</span>
                </div>
                <h3 className="text-lg font-bold text-[#0F172A]">{domain.name}</h3>
              </div>
            ))}
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://www.comptia.org/certifications/network" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white font-semibold transition-all"
            >
              Advance Your Career - Buy Network+ Training
            </a>
            <a 
              href="https://www.comptia.org/certifications/network" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border-2 border-[#2563EB] hover:bg-[#2563EB] hover:text-white text-[#2563EB] font-semibold transition-all"
            >
              Get Exam-Ready - Explore Bundles
            </a>
          </div>
        </div>
      </section>

      {/* Skills & Tools */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-2xl font-bold text-[#0F172A] mb-6" style={{ fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>
                Skills You'll Master
              </h2>
              <div className="flex flex-wrap gap-3">
                {skills.map((skill, idx) => (
                  <div key={idx} className="px-4 py-2 rounded-full bg-[#EFF6FF] border border-[#BFDBFE] text-[#1E40AF] font-medium text-sm">
                    {skill}
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h2 className="text-2xl font-bold text-[#0F172A] mb-6" style={{ fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>
                Tools & Technologies
              </h2>
              <div className="flex flex-wrap gap-3">
                {tools.map((tool, idx) => (
                  <div key={idx} className="px-4 py-2 rounded-full bg-[#F0FDF4] border border-[#BBF7D0] text-[#15803D] font-medium text-sm">
                    {tool}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Career Opportunities */}
      <section className="py-20 px-4 bg-[#F9FAFB]">
        <div className="max-w-[1200px] mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-8 text-center" style={{ fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>
            Career Opportunities
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {careers.map((career, idx) => (
              <div key={idx} className="p-6 rounded-[20px] bg-gradient-to-br from-[#EFF6FF] to-white border border-[#E5E7EB] hover:shadow-lg transition-all text-center group cursor-pointer">
                <Monitor size={40} className="text-[#2563EB] mx-auto mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="text-lg font-bold text-[#0F172A]">{career}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Prerequisites */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-[1200px] mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-8" style={{ fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>
            Prerequisites
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="p-6 rounded-[20px] bg-white border border-[#E5E7EB] hover:shadow-md transition-all">
              <CheckCircle size={40} className="text-[#16A34A] mx-auto mb-3" />
              <p className="text-[#0F172A] font-semibold">No prior IT experience required</p>
            </div>
            <div className="p-6 rounded-[20px] bg-white border border-[#E5E7EB] hover:shadow-md transition-all">
              <CheckCircle size={40} className="text-[#16A34A] mx-auto mb-3" />
              <p className="text-[#0F172A] font-semibold">Basic computer literacy</p>
            </div>
            <div className="p-6 rounded-[20px] bg-white border border-[#E5E7EB] hover:shadow-md transition-all">
              <CheckCircle size={40} className="text-[#16A34A] mx-auto mb-3" />
              <p className="text-[#0F172A] font-semibold">Willingness to learn hands-on</p>
            </div>
            <div className="p-6 rounded-[20px] bg-white border border-[#E5E7EB] hover:shadow-md transition-all">
              <CheckCircle size={40} className="text-[#16A34A] mx-auto mb-3" />
              <p className="text-[#0F172A] font-semibold">Commitment to study 1.5-2 hours daily</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4" style={{ background: 'linear-gradient(135deg, #020617 0%, #0B1120 100%)' }}>
        <div className="max-w-[1200px] mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Plus Jakarta Sans, Inter, sans-serif' }}>
            Ready to Start Your Journey?
          </h2>
          <p className="text-xl text-[#C7D2FE] mb-8">
            Join students transforming their careers with CareerLift.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#2563EB] hover:bg-[#1d4ed8] text-white font-semibold transition-all">
              Schedule Free Consultation
            </Link>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border-2 border-white/30 hover:border-white/60 hover:bg-white/5 text-white font-semibold transition-all">
              Enroll Now
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SystemNetworkEngineerPage;
